import type { AppContext } from 'next/app';
import isString from 'lodash/isString';
import omitBy from 'lodash/omitBy';
import pick from 'lodash/pick';
import isNil from 'lodash/isNil';

import { initializeApollo } from '@jerry-serverless/graphql-helpers/apollo/client';
import { decrypt } from '@jerry-serverless/crypto';
import { MAL_USER_KEY } from '@jerry-serverless/refi-common/const/encrypt';
import { initSSRClient } from '@jerry-serverless/graphql-helpers/apollo/client';
import { JA_SESSION_ID_COOKIE_NAME } from '@jerry-serverless/ui-analytics/lib/scripts/jerry-analytics-script';
import type { SignupChallengeStep } from '@jerry-serverless/enums/user_challenge';

import type { User } from '../apollo/graphql/user.fragment';
import type { MALViewerQueryData, ViewerQueryData } from '../apollo/graphql/viewer.query';
import { MAL_VIEWER_QUERY, VIEWER_QUERY } from '../apollo/graphql/viewer.query';
import { isInNeedQuotePages } from '../steps';
import type {
  SetPartnerAPIUserInput,
  SetPartnerAPIUserMutationData,
  CreatePartnerUserAndQuoteData,
  CreatePartnerUserAndQuoteInput,
} from '../apollo/graphql/partner.mutation';
import {
  SET_PARTNER_USER_API_MUTATION,
  CREATE_PARTNER_USER_AND_QUOTE_MUTATION,
} from '../apollo/graphql/partner.mutation';
import { defaultUserData } from '../apollo/graphql/user.fragment';

import { getAppQuotes } from './app_quotes';

const AUTH_COOKIE_NAME = 'jerryauth';

const EXISTING_USER = 'existing_user';

const getMalUserID = (context: AppContext): string | null => {
  return context.ctx.pathname === '/refimal' && isString(context.ctx.query.user)
    ? decrypt(decodeURIComponent(context.ctx.query.user), MAL_USER_KEY)
    : null;
};

/**
 * Setting user data from partner API user data.
 */
const initUserDataFromPartnerUser = (partnerUser): User => {
  if (partnerUser) {
    return {
      ...defaultUserData,
      ...partnerUser,
    };
  }
  return { ...defaultUserData, ...partnerUser };
};

/**
 * Return true, if current is in e2e test.
 */
export const isInE2ETestEnv = (cookie: Record<string, string>): boolean => {
  return process.env.APP_ENV === 'test' && cookie.jerry === 'e2eTest';
};

/**
 * Return current login user.
 */
export const getLoginUser = async (context: AppContext) => {
  let user: User | null = null;
  let signupChallengeStep: SignupChallengeStep | null = null;
  try {
    const malUserID = getMalUserID(context);
    const client = initializeApollo(context.ctx, {}, process.env.NEXT_PUBLIC_API_URL);
    // if the user was imported through MyAutoLoan before signup, load user data
    if (malUserID) {
      const resp = await client.query<MALViewerQueryData>({
        query: MAL_VIEWER_QUERY,
        variables: {
          malUserID,
        },
      });
      user = resp?.data?.malViewer?.user;
    } else if (isInNeedQuotePages(context.ctx.pathname)) {
      const result = await getAppQuotes(context.ctx);
      user = result?.user ?? null;
      signupChallengeStep = result?.signupChallengeStep || null;
    } else if (
      context.ctx.req?.headers.cookie?.includes(AUTH_COOKIE_NAME) ||
      context.ctx.req?.headers.cookie?.includes('jerry') // Old jerry2 session auth
    ) {
      const resp = await client.query<ViewerQueryData>({
        query: VIEWER_QUERY,
      });
      user = resp?.data?.viewer?.user;
      signupChallengeStep = resp?.data?.viewer?.signupChallengeStep || null;
    }
  } catch (err) {
    console.info(`[getAppData viewer error] ${context.ctx.req?.url} :`, err.message || err);
  }
  return { user, signupChallengeStep };
};

/**
 * Get user from partner.
 */
export const getUserFromPartner = async (
  context: AppContext,
  utoken: string,
  ptoken: string,
  cookieString: string,
  jasid: string
) => {
  let user: User | null = null;
  let jerryCookie = '';
  let jerryauth = '';

  const cookieStringToSend = `${cookieString}; ${JA_SESSION_ID_COOKIE_NAME}=${jasid}`;
  const ssrClient = initSSRClient(context.ctx, cookieStringToSend, process.env.NEXT_PUBLIC_API_URL);
  const partnerUserResp = await ssrClient.mutate<SetPartnerAPIUserMutationData, SetPartnerAPIUserInput>({
    mutation: SET_PARTNER_USER_API_MUTATION,
    variables: {
      input: { utoken, ptoken },
    },
  });

  // Set initial user value from API user data.
  // If valid (token was not expired, phone was verified), digest all data.
  if (partnerUserResp.data?.data?.setPartnerAPIUser?.user) {
    user = initUserDataFromPartnerUser(partnerUserResp.data.data.setPartnerAPIUser.user);

    // Sync jerry cookie since user will be landing directly on onboarding steps.
    jerryCookie = `jerry=${partnerUserResp.data.jerry}; `;
    if (partnerUserResp.data.jerryauth) {
      jerryauth = `jerryauth=${partnerUserResp.data.jerryauth}; path=/`;
    }
  } else {
    // Otherwise digest only 'id', 'name', 'phone', 'icvQuestions' data.
    const partnerUser = partnerUserResp.data?.data?.setPartnerAPIUser;
    user = initUserDataFromPartnerUser({
      id: partnerUser?.id,
      phone: partnerUser?.phone,
      name: partnerUser?.name,
      isHomeowner: partnerUser?.icvQuestions?.isHomeowner,
      signupParams: JSON.stringify(
        omitBy(
          pick(partnerUser?.icvQuestions, [
            'partnerSignup',
            'homeOwnership',
            'isAutoInsured',
            'autoInsuredLength',
            'claims',
            'violations',
          ]),
          (v) => isNil(v)
        )
      ),
    });
  }
  const partnerPrefillData = partnerUserResp.data?.data?.setPartnerAPIUser?.icvQuestions;
  return { user, jerryCookie, jerryauth, partnerPrefillData };
};

/**
 * Partner prefill quoting, create user and quote
 */
export const createPartnerUserAndQuote = async (
  context: AppContext,
  cookieString: string,
  pToken: string,
  clickID: string
) => {
  const ssrClient = initSSRClient(context.ctx, cookieString, process.env.NEXT_PUBLIC_API_URL);
  const createResp = await ssrClient.mutate<CreatePartnerUserAndQuoteData, CreatePartnerUserAndQuoteInput>({
    mutation: CREATE_PARTNER_USER_AND_QUOTE_MUTATION,
    variables: {
      pToken,
      clickID,
    },
  });
  return createResp;
};

export const setExistingUser = (id: string) => {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(`${EXISTING_USER}_${id}`, 'true');
  }
};

export const checkExistingUser = (id: string) => {
  if (typeof sessionStorage !== 'undefined') {
    return !!sessionStorage.getItem(`${EXISTING_USER}_${id}`);
  }
  return false;
};
